import React from "react";
import { ArrowUp, ArrowDown, X } from "lucide-react";

/**
 * Compact sort-state manager for clickable column headers.
 *
 * Internal shape: Array<{ path: string, dir: "asc" | "desc" }>
 * Path examples: "A:Debit", "B:Amount", "rule_name".
 *
 *    const { spec, onClick, toQuery, clear, findIndex, dirOf } = useSortState();
 *
 *    <SortHeader label="Debit" path="A:Debit" spec={spec} onClick={onClick} />
 *    <button onClick={clear} disabled={spec.length === 0}>Clear sort</button>
 *
 * Pass `toQuery()` to the `sort` query param of the fetch. Empty = no param.
 */
export function useSortState() {
  const [spec, setSpec] = React.useState([]);

  const findIndex = React.useCallback((path) => spec.findIndex(s => s.path === path), [spec]);
  const dirOf = React.useCallback((path) => {
    const i = findIndex(path);
    return i >= 0 ? spec[i].dir : null;
  }, [spec, findIndex]);

  // Click cycling per column: none → asc → desc → removed
  // Shift-click appends without disturbing others; normal click on a NEW column also appends
  // Normal click on an ALREADY-sorted column cycles its direction (or removes it on the third click)
  const onClick = React.useCallback((path, opts = {}) => {
    setSpec(prev => {
      const idx = prev.findIndex(s => s.path === path);
      if (idx >= 0) {
        const cur = prev[idx];
        const next = [...prev];
        if (cur.dir === "asc")      next[idx] = { path, dir: "desc" };
        else if (cur.dir === "desc") next.splice(idx, 1);
        return next;
      }
      return [...prev, { path, dir: "asc" }];
    });
  }, []);

  const clear = React.useCallback(() => setSpec([]), []);

  const toQuery = React.useCallback(
    () => spec.map(s => `${s.path}:${s.dir}`).join(","),
    [spec]
  );

  return { spec, onClick, clear, findIndex, dirOf, toQuery, setSpec };
}

export function SortHeader({ label, path, spec, onClick, style = {}, testidPrefix = "sort" }) {
  const idx = spec.findIndex(s => s.path === path);
  const dir = idx >= 0 ? spec[idx].dir : null;
  return (
    <th
      data-testid={`${testidPrefix}-${path.replace(/[^a-zA-Z0-9]/g, "-")}`}
      className="table-header"
      onClick={() => onClick(path)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", ...style }}
      title={idx >= 0 ? `Sort level ${idx + 1} (${dir})` : "Click to sort"}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
        {label}
        {dir === "asc" && <ArrowUp size={11} />}
        {dir === "desc" && <ArrowDown size={11} />}
        {idx >= 0 && (
          <span style={{ fontSize: "0.65rem", background: "var(--sage-light)", color: "var(--sage)", border: "1px solid var(--sage-border)", borderRadius: "9999px", padding: "0 0.3rem", fontWeight: 600 }}>
            {idx + 1}
          </span>
        )}
      </span>
    </th>
  );
}

export function SortClearButton({ spec, onClear, testid = "sort-clear" }) {
  if (!spec || spec.length === 0) return null;
  return (
    <button
      data-testid={testid}
      onClick={onClear}
      className="btn-secondary"
      title={"Clear multi-level sort: " + spec.map((s, i) => `${i + 1}. ${s.path} ${s.dir}`).join(" · ")}
      style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", fontSize: "0.72rem", padding: "0.2rem 0.55rem" }}
    >
      <X size={11} /> Clear sort ({spec.length})
    </button>
  );
}

export function SortSummary({ spec }) {
  if (!spec || spec.length === 0) return null;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontSize: "0.7rem", color: "var(--text-muted)" }}>
      <span>Sort:</span>
      {spec.map((s, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: "var(--bg-subtle)", padding: "0.1rem 0.4rem", borderRadius: "9999px", border: "1px solid var(--border-default)" }}>
          <span style={{ fontWeight: 600 }}>{i + 1}.</span> {s.path}
          {s.dir === "asc" ? <ArrowUp size={9} /> : <ArrowDown size={9} />}
        </span>
      ))}
    </div>
  );
}

/**
 * SortPicker — toolbar-style UI for multi-level sort.
 *   <SortPicker columns={[{value:"A:Debit", label:"Debit (A)"}, ...]}
 *               spec={spec} setSpec={setSpec} />
 */
export function SortPicker({ columns = [], spec, setSpec, testidPrefix = "sort-picker" }) {
  const [picking, setPicking] = React.useState(false);
  const add = (path) => {
    if (spec.find(s => s.path === path)) return;  // already added
    setSpec([...spec, { path, dir: "asc" }]);
    setPicking(false);
  };
  const toggleDir = (i) => {
    setSpec(spec.map((s, j) => j === i ? { ...s, dir: s.dir === "asc" ? "desc" : "asc" } : s));
  };
  const remove = (i) => setSpec(spec.filter((_, j) => j !== i));
  const clear = () => setSpec([]);

  return (
    <div
      data-testid={`${testidPrefix}-bar`}
      style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", flexWrap: "wrap", fontSize: "0.72rem" }}
    >
      <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>Sort:</span>
      {spec.map((s, i) => (
        <span key={i} data-testid={`${testidPrefix}-chip-${i}`}
          style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", background: "var(--sage-light)", color: "var(--sage)", padding: "0.15rem 0.5rem", borderRadius: "9999px", border: "1px solid var(--sage-border)" }}>
          <span style={{ fontWeight: 700 }}>{i + 1}</span>
          <span style={{ fontWeight: 500 }}>{s.path}</span>
          <button onClick={() => toggleDir(i)} title="Toggle direction"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "inherit" }}>
            {s.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
          </button>
          <button onClick={() => remove(i)} title="Remove"
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: "inherit" }}>
            <X size={10} />
          </button>
        </span>
      ))}
      <div style={{ position: "relative" }}>
        <button data-testid={`${testidPrefix}-add`}
          onClick={() => setPicking(p => !p)}
          className="btn-secondary"
          style={{ fontSize: "0.7rem", padding: "0.18rem 0.55rem", display: "inline-flex", alignItems: "center", gap: "0.2rem" }}>
          + {spec.length === 0 ? "Sort by…" : "Add level"}
        </button>
        {picking && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 20, background: "white", border: "1px solid var(--border-default)", borderRadius: "0.4rem", boxShadow: "0 6px 16px rgba(0,0,0,0.12)", maxHeight: "280px", overflowY: "auto", minWidth: "200px" }}>
            {columns.length === 0 ? (
              <div style={{ padding: "0.5rem 0.75rem", color: "var(--text-muted)" }}>No columns</div>
            ) : columns.map(c => (
              <button key={c.value} onClick={() => add(c.value)} disabled={!!spec.find(s => s.path === c.value)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "0.3rem 0.6rem", border: "none", background: "transparent", cursor: "pointer", fontSize: "0.76rem", color: spec.find(s => s.path === c.value) ? "var(--text-muted)" : "var(--text-primary)" }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--bg-subtle)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                {c.label || c.value}
              </button>
            ))}
          </div>
        )}
      </div>
      {spec.length > 0 && (
        <button data-testid={`${testidPrefix}-clear`}
          onClick={clear}
          style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.7rem", display: "inline-flex", alignItems: "center", gap: "0.2rem", padding: "0.15rem 0.4rem" }}>
          <X size={11} /> Clear
        </button>
      )}
    </div>
  );
}
